import type { ApiConfig } from '@api/types';
import { typedMethodPost } from '@api/utils';
import type {
  GlobalSearchResponseDto,
  ProductionOrderSearchResponseDto,
  PartSearchResponseDto,
  ProductionOperationSearchResponseDto,
  ProductionBatchSearchResponseDto,
  NcFileSearchResponseDto,
  ToolSearchResponseDto,
  DocumentSearchResponseDto,
  RecipeSearchResponseDto,
  RecipeOperationSearchResponseDto,
  MachineEventSearchResponseDto,
  MachineEventCycleSearchResponseDto,
} from '../../generated/models';
import type { SearchSuggestionRequest, SuggestionResponse as SuggestionResponseLocal } from './types';

// Domain types
import type {
  GlobalSearchResponse,
  ProductionOrderSearchResponse,
  PartSearchResponse,
  ProductionOperationSearchResponse,
  ProductionBatchSearchResponse,
  NcFileSearchResponse,
  ToolSearchResponse,
  DocumentSearchResponse,
  RecipeSearchResponse,
  RecipeOperationSearchResponse,
  MachineEventSearchResponse,
  MachineEventCycleSearchResponse,
  SuggestionResponse,
} from '../../domain/types';

// Mappers
import {
  toGlobalSearchResponse,
  toProductionOrderSearchResponse,
  toPartSearchResponse,
  toProductionOperationSearchResponse,
  toProductionBatchSearchResponse,
  toNcFileSearchResponse,
  toToolSearchResponse,
  toDocumentSearchResponse,
  toRecipeSearchResponse,
  toRecipeOperationSearchResponse,
  toMachineEventSearchResponse,
  toMachineEventCycleSearchResponse,
  toSuggestionResponse,
} from '../../domain/mappers';

export default ({ baseURL }: ApiConfig) => ({
  globalSuggestion: typedMethodPost<SearchSuggestionRequest, SuggestionResponseLocal, SuggestionResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/global/suggestion`, {
        method: 'POST',
        ...data,
      }),
    toSuggestionResponse
  ),
  productionOrdersSuggestion: typedMethodPost<SearchSuggestionRequest, SuggestionResponseLocal, SuggestionResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/productionOrders/suggestion`, {
        method: 'POST',
        ...data,
      }),
    toSuggestionResponse
  ),
  partsSuggestion: typedMethodPost<SearchSuggestionRequest, SuggestionResponseLocal, SuggestionResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/parts/suggestion`, {
        method: 'POST',
        ...data,
      }),
    toSuggestionResponse
  ),
  operationsSuggestion: typedMethodPost<SearchSuggestionRequest, SuggestionResponseLocal, SuggestionResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/productionOperations/suggestion`, {
        method: 'POST',
        ...data,
      }),
    toSuggestionResponse
  ),
  productionBatchesSuggestion: typedMethodPost<SearchSuggestionRequest, SuggestionResponseLocal, SuggestionResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/productionBatches/suggestion`, {
        method: 'POST',
        ...data,
      }),
    toSuggestionResponse
  ),
  ncFilesSuggestion: typedMethodPost<SearchSuggestionRequest, SuggestionResponseLocal, SuggestionResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/ncFiles/suggestion`, {
        method: 'POST',
        ...data,
      }),
    toSuggestionResponse
  ),
  toolsSuggestion: typedMethodPost<SearchSuggestionRequest, SuggestionResponseLocal, SuggestionResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/tools/suggestion`, {
        method: 'POST',
        ...data,
      }),
    toSuggestionResponse
  ),
  documentsSuggestion: typedMethodPost<SearchSuggestionRequest, SuggestionResponseLocal, SuggestionResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/documents/suggestion`, {
        method: 'POST',
        ...data,
      }),
    toSuggestionResponse
  ),
  globalSearch: typedMethodPost<SearchSuggestionRequest, GlobalSearchResponseDto, GlobalSearchResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/global`, {
        method: 'POST',
        ...data,
      }),
    toGlobalSearchResponse
  ),
  productionOrdersSearch: typedMethodPost<
    SearchSuggestionRequest,
    ProductionOrderSearchResponseDto,
    ProductionOrderSearchResponse
  >(
    (data) =>
      fetch(`${baseURL}/v1/search/productionOrders`, {
        method: 'POST',
        ...data,
      }),
    toProductionOrderSearchResponse
  ),
  partsSearch: typedMethodPost<SearchSuggestionRequest, PartSearchResponseDto, PartSearchResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/parts`, {
        method: 'POST',
        ...data,
      }),
    toPartSearchResponse
  ),
  operationsSearch: typedMethodPost<
    SearchSuggestionRequest,
    ProductionOperationSearchResponseDto,
    ProductionOperationSearchResponse
  >(
    (data) =>
      fetch(`${baseURL}/v1/search/productionOperations`, {
        method: 'POST',
        ...data,
      }),
    toProductionOperationSearchResponse
  ),
  productionBatchesSearch: typedMethodPost<
    SearchSuggestionRequest,
    ProductionBatchSearchResponseDto,
    ProductionBatchSearchResponse
  >(
    (data) =>
      fetch(`${baseURL}/v1/search/productionBatches`, {
        method: 'POST',
        ...data,
      }),
    toProductionBatchSearchResponse
  ),
  ncFilesSearch: typedMethodPost<SearchSuggestionRequest, NcFileSearchResponseDto, NcFileSearchResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/ncFiles`, {
        method: 'POST',
        ...data,
      }),
    toNcFileSearchResponse
  ),
  toolsSearch: typedMethodPost<SearchSuggestionRequest, ToolSearchResponseDto, ToolSearchResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/tools`, {
        method: 'POST',
        ...data,
      }),
    toToolSearchResponse
  ),
  documentsSearch: typedMethodPost<SearchSuggestionRequest, DocumentSearchResponseDto, DocumentSearchResponse>(
    (data) =>
      fetch(`${baseURL}/v2/search/documents`, {
        method: 'POST',
        ...data,
      }),
    toDocumentSearchResponse
  ),
  productionBatches: typedMethodPost<
    SearchSuggestionRequest,
    ProductionBatchSearchResponseDto,
    ProductionBatchSearchResponse
  >(
    (data) =>
      fetch(`${baseURL}/v1/search/productionBatches`, {
        method: 'POST',
        ...data,
      }),
    toProductionBatchSearchResponse
  ),
  recipesSearch: typedMethodPost<SearchSuggestionRequest, RecipeSearchResponseDto, RecipeSearchResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/recipes`, {
        method: 'POST',
        ...data,
      }),
    toRecipeSearchResponse
  ),
  recipeOperationsSearch: typedMethodPost<
    SearchSuggestionRequest,
    RecipeOperationSearchResponseDto,
    RecipeOperationSearchResponse
  >(
    (data) =>
      fetch(`${baseURL}/v1/search/recipeOperations`, {
        method: 'POST',
        ...data,
      }),
    toRecipeOperationSearchResponse
  ),
  machineEvents: typedMethodPost<SearchSuggestionRequest, MachineEventSearchResponseDto, MachineEventSearchResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/machineEvents`, {
        method: 'POST',
        ...data,
      }),
    toMachineEventSearchResponse
  ),
  machineEventCycles: typedMethodPost<
    SearchSuggestionRequest,
    MachineEventCycleSearchResponseDto,
    MachineEventCycleSearchResponse
  >(
    (data) =>
      fetch(`${baseURL}/v1/search/machineEventCycles`, {
        method: 'POST',
        ...data,
      }),
    toMachineEventCycleSearchResponse
  ),
});

// Re-export domain types for consumers
export type {
  GlobalSearchResponse,
  ProductionOrderSearchResponse,
  PartSearchResponse,
  ProductionOperationSearchResponse,
  ProductionBatchSearchResponse,
  NcFileSearchResponse,
  ToolSearchResponse,
  DocumentSearchResponse,
  RecipeSearchResponse,
  RecipeOperationSearchResponse,
  MachineEventSearchResponse,
  MachineEventCycleSearchResponse,
  SuggestionResponse,
};
export type { SearchSuggestionRequest } from './types';
